import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Card, CardContent, List, Divider, Button } from '@mui/material';
import { fetchTeacherNotes } from '../../redux/notesRelated/notesSlice';
import { motion } from 'framer-motion';

const TeacherNotes = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector(state => state.user);
    const { notes, loading, error } = useSelector(state => state.notes);

    useEffect(() => {
        if (currentUser && currentUser._id) {
            dispatch(fetchTeacherNotes(currentUser._id))
                .catch((error) => { 
                    console.error('Error fetching notes:', error);
                });
        }
    }, [dispatch, currentUser]);

    return (
        <Box sx={styles.container}>
            {/* Work in Progress Message */}
            <Typography variant="h5" sx={styles.wipMessage}>
                🚧 Work in Progress 🚧
            </Typography>

            <Typography variant="h4" sx={styles.title}>
                Sent Notes
            </Typography>

            {loading && <Typography>Loading...</Typography>}
            {error && <Typography color="error">Error: {error}</Typography>}

            <List sx={styles.list}>
                {notes.map((note) => (
                    <motion.div
                        key={note._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <Card sx={styles.card}>
                            <CardContent>
                                <Typography variant="h6" sx={styles.noteTitle}>
                                    {note.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={styles.date}>
                                    {new Date(note.date).toLocaleDateString()}
                                </Typography>
                                <Typography variant="body1" sx={styles.content}>
                                    {note.content}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Sent to: {note.studentId?.name || "Unknown"}
                                </Typography>
                                <Box sx={styles.buttonContainer}>
                                    <Button 
                                        variant="contained" 
                                        sx={styles.button}
                                        onClick={() => alert('Edit Note')}
                                    >
                                        Edit
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        sx={styles.button}
                                        onClick={() => alert('Delete Note')}
                                    >
                                        Delete
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                        <Divider sx={styles.divider} />
                    </motion.div>
                ))}
            </List>
        </Box>
    );
};

const styles = {
    container: {
        maxWidth: 800,
        mx: 'auto',
        p: 3,
        backgroundColor: '#f0f8ff', // Light background for contrast
    },
    wipMessage: {
        mb: 2,
        textAlign: 'center',
        fontWeight: 'bold',
        color: '#ff6347', // Tomato color for visibility
    },
    title: {
        mb: 4,
        fontWeight: 'bold',
        color: '#008080', // Teal color
        textAlign: 'center',
        fontSize: { xs: '1.8rem', sm: '2.2rem' }, // Responsive font size
    },
    list: {
        width: '100%',
    },
    card: {
        mb: 2,
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        transition: 'transform 0.3s ease-in-out',
        '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.2)',
        },
    },
    noteTitle: {
        fontWeight: 'bold',
        mb: 1,
        color: '#008080', // Teal color
    },
    date: {
        mb: 1,
        color: '#ffd700', // Gold color
    },
    content: {
        mb: 2,
        color: '#333', // Dark color for readability
    },
    divider: {
        my: 2,
        backgroundColor: '#008080', // Teal color
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
        mt: 2,
    },
    button: {
        backgroundColor: '#ffd700', // Gold color
        color: '#000',
        '&:hover': {
            backgroundColor: '#008080', // Teal color on hover
            color: '#fff',
        },
    },
};

export default TeacherNotes;